import { useEffect, useMemo } from 'react';
import { DownloadInfo, DownloadOption } from 'types';
import { SongType } from 'services/api/entities';
import { AudioDownloadRenderingStatus } from 'services/api/entities/audioDownloadRendering';
import {
  useDownloadLicense,
  useDownloadSong,
  useDownloadStems,
  useDownloadSamplePack,
} from 'services/hooks/download';
import {
  CopyrightViolationModal,
  CopyrightViolationType,
} from 'components/Modals/CopyrightViolationModal';
import {
  DownloadStemsLimitDialog,
  DownloadSongLimitDialog,
  DownloadSamplePackLimitDialog,
} from '../DownloadLimitDialogs';
import StemRenderingStartedDialog from './components/StemRenderingStartedDialog';
import SamplePackRenderingStartedDialog from './components/SamplePackRenderingStartedDialog';
import DownloadSelectionDialog from './components/DownloadSelectionDialog';

interface PropsType {
  songId: string;
  songType: SongType;
  isOpen: boolean;
  onClose: () => void;
  addToLibrary: () => void;
  analyticsTrackDownload: (downloadData: DownloadInfo) => void;
}

/**
 * A container for all the possible dialogs that can be displayed in the download flow
 * this relates mainly to the DownloadSelectionDialog but also dialogs indicating download limits,
 * copyright violations and other
 */

const DownloadDialog = ({
  songId,
  songType,
  isOpen,
  onClose,
  addToLibrary,
  analyticsTrackDownload,
}: PropsType) => {
  const {
    downloadLicense,
    loading: licenseDownloadLoading,
    isCopyrightViolationModalOpen,
    closeCopyrightViolationModal,
  } = useDownloadLicense({ songId, onSuccess: onClose });

  const handleSongDownloadSuccess = (downloadOption: DownloadOption) => {
    analyticsTrackDownload({
      type: downloadOption,
    });
    onClose();
  };

  const {
    downloadSong,
    loading: songDownloadLoading,
    isLimitModalOpen: isDownloadSongLimitModalOpen,
    closeLimitModal: closeSongDownloadLimitModal,
  } = useDownloadSong({ songId, onSuccess: handleSongDownloadSuccess });

  const handleStemsRenderingSuccess = (
    status: AudioDownloadRenderingStatus
  ) => {
    analyticsTrackDownload({
      type: DownloadOption.Stems,
      status,
    });
    onClose();
  };

  const {
    downloadStems,
    loading: stemsDownloadLoading,
    isStemRenderingDialogOpen,
    closeStemRenderingDialog,
    isLimitDialogOpen: isDownloadStemsLimitDialogOpen,
    closeLimitDialog: closeStemsDownloadLimitDialog,
  } = useDownloadStems({ songId, onSuccess: handleStemsRenderingSuccess });

  const handleSamplePackRenderingSuccess = (
    status: AudioDownloadRenderingStatus
  ) => {
    analyticsTrackDownload({
      type: DownloadOption.SamplePack,
      status,
    });
    onClose();
  };

  const {
    downloadSamplePack,
    loading: samplePackDownloadLoading,
    isSamplePackRenderingDialogOpen,
    closeSamplePackRenderingDialog,
    isLimitDialogOpen: isDownloadSamplePackLimitDialogOpen,
    closeLimitDialog: closeSamplePackDownloadLimitDialog,
  } = useDownloadSamplePack({
    songId,
    onSuccess: handleSamplePackRenderingSuccess,
  });

  const isLoading = useMemo(
    () =>
      licenseDownloadLoading ||
      songDownloadLoading ||
      stemsDownloadLoading ||
      samplePackDownloadLoading,
    [
      licenseDownloadLoading,
      songDownloadLoading,
      stemsDownloadLoading,
      samplePackDownloadLoading,
    ]
  );

  /** Adds songs to library when users open the download modal. */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    addToLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = (downloadOption: DownloadOption) => {
    if (
      downloadOption === DownloadOption.Mp3 ||
      downloadOption === DownloadOption.Wav
    ) {
      downloadSong(downloadOption);
      return;
    }
    if (downloadOption === DownloadOption.Stems) {
      downloadStems();
      return;
    }
    if (downloadOption === DownloadOption.SamplePack) {
      downloadSamplePack();
      return;
    }
    if (downloadOption === DownloadOption.PdfLicense) {
      downloadLicense();
    }
  };

  const handleClose = (closeAction: () => void) => {
    closeAction();
    onClose();
  };

  if (isStemRenderingDialogOpen) {
    return (
      <StemRenderingStartedDialog
        isOpen={isStemRenderingDialogOpen}
        onClose={() => handleClose(closeStemRenderingDialog)}
      />
    );
  }

  if (isSamplePackRenderingDialogOpen) {
    return (
      <SamplePackRenderingStartedDialog
        isOpen={isSamplePackRenderingDialogOpen}
        onClose={() => handleClose(closeSamplePackRenderingDialog)}
      />
    );
  }

  if (isDownloadStemsLimitDialogOpen) {
    return (
      <DownloadStemsLimitDialog
        isOpen={isDownloadStemsLimitDialogOpen}
        onClose={() => handleClose(closeStemsDownloadLimitDialog)}
      />
    );
  }

  if (isDownloadSamplePackLimitDialogOpen) {
    return (
      <DownloadSamplePackLimitDialog
        isOpen={isDownloadSamplePackLimitDialogOpen}
        onClose={() => handleClose(closeSamplePackDownloadLimitDialog)}
      />
    );
  }

  if (isDownloadSongLimitModalOpen) {
    return (
      <DownloadSongLimitDialog
        isOpen={isDownloadSongLimitModalOpen}
        onClose={() => handleClose(closeSongDownloadLimitModal)}
      />
    );
  }

  if (isCopyrightViolationModalOpen) {
    return (
      <CopyrightViolationModal
        variant={CopyrightViolationType.PDF_LICENSE}
        isOpen={isCopyrightViolationModalOpen}
        onClose={() => handleClose(closeCopyrightViolationModal)}
      />
    );
  }

  return (
    <DownloadSelectionDialog
      songType={songType}
      isLoading={isLoading}
      isOpen={isOpen}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

export default DownloadDialog;
